/**
 * commandCenter 待办事项子模块
 *
 * 管理待办事项的增删改查和角标动画。
 */
import type { IntelligentFeed, TodoItem, TodoPriority } from '@/types/bi'
import { computed, ref } from 'vue'

/**
 * 创建待办事项子模块的状态和操作
 */
export function useTodoSlice() {
  // ===== State =====
  const todoItems = ref<TodoItem[]>([])
  const todoBadgeAnimating = ref(false)

  // ===== Getters =====
  const unfinishedTodoCount = computed(() => {
    return todoItems.value.filter(t => !t.done).length
  })

  // ===== Actions =====

  // 触发待办角标动画
  function triggerTodoBadgeAnimation() {
    todoBadgeAnimating.value = true
    setTimeout(() => {
      todoBadgeAnimating.value = false
    }, 600)
  }

  // 添加待办事项
  function addTodoItem(item: Omit<TodoItem, 'id' | 'createdAt'>) {
    const newItem: TodoItem = {
      ...item,
      id: `todo-${Date.now()}`,
      createdAt: new Date().toISOString()
    }
    todoItems.value.unshift(newItem)

    // 触发角标动画
    triggerTodoBadgeAnimation()

    return newItem
  }

  // 从智能推送添加待办
  function addTodoFromFeed(feed: IntelligentFeed) {
    // 根据推送的 severity 映射优先级
    const priorityMap: Record<string, TodoPriority> = {
      high: 'high',
      medium: 'medium',
      low: 'low'
    }
    const priority = priorityMap[feed.severity || 'medium'] || 'medium'

    const newItem: TodoItem = {
      id: `todo-${Date.now()}`,
      text: `${feed.title}: ${feed.content.slice(0, 50)}${feed.content.length > 50 ? '...' : ''}`,
      priority,
      time: '今天',
      done: false,
      source: {
        type: 'smart-push',
        feedId: feed.id,
        feedTitle: feed.title,
        feedType: feed.type,
        feedContent: feed.content
      },
      createdAt: new Date().toISOString()
    }

    todoItems.value.unshift(newItem)

    // 触发角标动画
    triggerTodoBadgeAnimation()

    return newItem
  }

  // 切换待办完成状态
  function toggleTodoItem(id: string) {
    const item = todoItems.value.find(t => t.id === id)
    if (item) {
      item.done = !item.done
    }
  }

  // 删除待办事项
  function removeTodoItem(id: string) {
    const index = todoItems.value.findIndex(t => t.id === id)
    if (index > -1) {
      todoItems.value.splice(index, 1)
    }
  }

  // 根据 feedId 获取待办项
  function getTodoByFeedId(feedId: string) {
    return todoItems.value.find(t => t.source?.feedId === feedId)
  }

  return {
    // State
    todoItems,
    todoBadgeAnimating,
    // Getters
    unfinishedTodoCount,
    // Actions
    addTodoItem,
    addTodoFromFeed,
    toggleTodoItem,
    removeTodoItem,
    getTodoByFeedId,
    triggerTodoBadgeAnimation,
  }
}
